import { Fragment, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useOutlets } from '@/hooks/useOutlets';
import { useProducts } from '@/hooks/useProducts';
import { endOfDay, format, startOfDay } from 'date-fns';
import type { Transaction, PaymentStatus } from '@photobooth/types';

const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'expired', label: 'Expired' },
];

const STATUS_BADGE_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  queued: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE_COLORS[status] ?? 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
}

const PAYMENT_OPTIONS = ['pending', 'completed', 'failed', 'expired', 'cancelled'] as const;
const CAPTURE_OPTIONS = ['pending', 'in_progress', 'completed'] as const;
const EDIT_OPTIONS = ['pending', 'in_progress', 'completed'] as const;
const PRINT_OPTIONS = ['pending', 'queued', 'completed', 'failed'] as const;

type StatusDropdownProps = {
  currentStatus: string;
  options: readonly string[];
  onSelect: (value: string) => void;
  isLoading?: boolean;
};

function StatusDropdown({ currentStatus, options, onSelect, isLoading }: StatusDropdownProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | null>(null);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownMaxHeight = 160;
      const openAbove = spaceBelow < dropdownMaxHeight;
      setDropdownStyle({
        left: rect.left,
        top: openAbove ? undefined : rect.bottom + 4,
        bottom: openAbove ? window.innerHeight - rect.top + 4 : undefined,
      });
    } else {
      setDropdownStyle(null);
    }
  }, [open]);

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={isLoading}
        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <StatusBadge status={currentStatus} />
        {isLoading ? (
          <span className="ml-1 h-3 w-3 animate-spin rounded-full border border-gray-300 border-t-primary" />
        ) : (
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      {open && dropdownStyle && (
        <>
          <div className="fixed inset-0 z-10" aria-hidden onClick={() => setOpen(false)} />
          <ul
            className="fixed z-20 max-h-40 w-32 overflow-auto rounded border border-gray-200 bg-white py-1 shadow-lg"
            style={dropdownStyle}
            role="listbox"
          >
            {options.map((opt) => (
              <li key={opt}>
                <button
                  type="button"
                  role="option"
                  aria-selected={opt === currentStatus}
                  onClick={() => {
                    onSelect(opt);
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100 ${opt === currentStatus ? 'bg-gray-50 font-medium' : ''}`}
                >
                  {opt}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function TransactionsPage() {
  const { outlets } = useOutlets();
  const { products } = useProducts();
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [selectedOutletId, setSelectedOutletId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [hasAddonsFilter, setHasAddonsFilter] = useState<'all' | 'with_addons'>('all');
  const [search, setSearch] = useState('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const outletMap = new Map(outlets.map((o) => [o.outletId, o.name]));
  const productMap = new Map(products.map((p) => [p.productId, p.name]));

  const filteredTransactions =
    hasAddonsFilter === 'with_addons'
      ? transactions.filter((t) => t.addons && t.addons.length > 0)
      : transactions;

  const fetchTransactions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const startDate =
        dateRange.startDate ? startOfDay(new Date(dateRange.startDate)).toISOString() : undefined;
      const endDate =
        dateRange.endDate ? endOfDay(new Date(dateRange.endDate)).toISOString() : undefined;

      const response = await api.transactions.getList({
        outletId: selectedOutletId || undefined,
        startDate,
        endDate,
        paymentStatus: paymentStatus || undefined,
        search: search.trim() || undefined,
        limit: 100,
      });
      if (response.success && response.data) {
        const data = (response.data as any).data ?? response.data;
        setTransactions(Array.isArray(data) ? data : []);
      } else {
        setError('Failed to fetch transactions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const updatePaymentStatus = async (tx: Transaction, value: string) => {
    setUpdatingId(tx.transactionId);
    try {
      const response = await api.transactions.updatePaymentStatus(tx.transactionId, value as PaymentStatus);
      if (response.success && response.data) {
        setTransactions((prev) =>
          prev.map((t) =>
            t.transactionId === tx.transactionId
              ? { ...t, payment: { ...t.payment, status: value as PaymentStatus } }
              : t
          )
        );
      } else {
        setError(response.error?.message ?? 'Failed to update payment status');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update payment status');
    } finally {
      setUpdatingId(null);
    }
  };

  const updateCaptureEditPrintStatus = async (
    tx: Transaction,
    field: 'captureStatus' | 'editStatus' | 'printStatus',
    value: string
  ) => {
    setUpdatingId(tx.transactionId);
    try {
      const response = await api.transactions.updateStatus(tx.transactionId, { [field]: value as any });
      if (response.success && response.data) {
        setTransactions((prev) =>
          prev.map((t) =>
            t.transactionId === tx.transactionId ? { ...t, [field]: value } : t
          )
        );
      } else {
        setError(response.error?.message ?? 'Failed to update status');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-text">Transactions</h1>
        <p className="text-text-muted mt-1">View and search orders across all outlets</p>
      </div>

      <div className="bg-surface rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-bold text-text mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Outlet</label>
            <select
              value={selectedOutletId}
              onChange={(e) => setSelectedOutletId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Outlets</option>
              {outlets.map((outlet) => (
                <option key={outlet.outletId} value={outlet.outletId}>
                  {outlet.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Payment Status</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {PAYMENT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Add-ons</label>
            <select
              value={hasAddonsFilter}
              onChange={(e) => setHasAddonsFilter(e.target.value as 'all' | 'with_addons')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All</option>
              <option value="with_addons">With add-ons only</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Search</label>
            <input
              type="text"
              placeholder="Customer, ID, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={fetchTransactions}
            disabled={isLoading}
            className="bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : 'Fetch'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-surface rounded-lg shadow overflow-hidden">
        {isLoading && transactions.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            No transactions found. Adjust filters and click Fetch to load data.
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            No transactions with add-ons found. Try adjusting filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Outlet</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Capture</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Edit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Print</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((t) => (
                  <Fragment key={t.transactionId}>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-text">
                        <div className="flex items-center gap-2">
                          {t.addons && t.addons.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(t.transactionId)}
                              className="p-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                              aria-label={expandedIds.has(t.transactionId) ? 'Collapse' : 'Expand'}
                            >
                              <svg
                                className={`w-4 h-4 transition-transform ${expandedIds.has(t.transactionId) ? 'rotate-90' : ''}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                          ) : null}
                          {t.transactionId}
                        </div>
                      </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                      {format(new Date(t.createdAt), 'dd MMM yyyy HH:mm')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                      {outletMap.get(t.outletId) ?? t.outletId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                      {productMap.get(t.productId) ?? t.productId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                      {t.customer?.name ?? '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                      Rp {((t.payment?.amount ?? 0) + (t.addons?.reduce((s, a) => s + a.totalPrice, 0) ?? 0)).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusDropdown
                        currentStatus={t.payment?.status ?? 'pending'}
                        options={PAYMENT_OPTIONS}
                        onSelect={(v) => updatePaymentStatus(t, v)}
                        isLoading={updatingId === t.transactionId}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusDropdown
                        currentStatus={t.captureStatus ?? 'pending'}
                        options={CAPTURE_OPTIONS}
                        onSelect={(v) => updateCaptureEditPrintStatus(t, 'captureStatus', v)}
                        isLoading={updatingId === t.transactionId}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusDropdown
                        currentStatus={t.editStatus ?? 'pending'}
                        options={EDIT_OPTIONS}
                        onSelect={(v) => updateCaptureEditPrintStatus(t, 'editStatus', v)}
                        isLoading={updatingId === t.transactionId}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusDropdown
                        currentStatus={t.printStatus ?? 'pending'}
                        options={PRINT_OPTIONS}
                        onSelect={(v) => updateCaptureEditPrintStatus(t, 'printStatus', v)}
                        isLoading={updatingId === t.transactionId}
                      />
                    </td>
                  </tr>
                  {expandedIds.has(t.transactionId) && t.addons && t.addons.length > 0 && (
                    <tr className="bg-gray-50">
                      <td colSpan={10} className="px-6 py-3 text-sm">
                        <div className="font-medium text-text-muted mb-2">Session product</div>
                        <ul className="space-y-1 mb-3">
                          <li className="flex gap-4 text-text">
                            <span>{t.productName ?? productMap.get(t.productId) ?? t.productId}</span>
                            <span>× 1</span>
                            <span>Rp {(t.payment?.amount ?? 0).toLocaleString()}</span>
                          </li>
                        </ul>
                        <div className="font-medium text-text-muted mb-2">Add-ons</div>
                        <ul className="space-y-1">
                          {t.addons.map((a) => (
                            <li key={a.transactionAddonId} className="flex gap-4 text-text">
                              <span>{a.productName ?? productMap.get(a.productId) ?? a.productId}</span>
                              <span>× {a.quantity}</span>
                              <span>Rp {a.totalPrice.toLocaleString()}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
